import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Button, makeStyles } from '@fluentui/react-components';
import { EditRegular } from '@fluentui/react-icons';
import StackColumn from '../../common/StackColumn';
import Card from '../../common/card/Card';
import CanAccess from '../../common/canAccess/CanAccess';
import LoadingErrorMessage from '../../common/errorContent/LoadingErrorMessage';
import SpanWithLabel from '../../common/SpanWithLabel';
import {
  bodyContentContainer,
  iconButtonStyles,
} from '../../../app/common/styles/CommonStyleObjects';
import { GET_ENTITY_TERMINATION_OVERVIEW } from '../../../utils/statApi/EntityTerminationApi';
import FeatureFlagged from '../../common/featureFlagged/featureFlagged';
import EntityTerminationStatusEditPanel from './EntityTerminationStatusEditPanel';
import EntityTerminationContactsEditPanel from './EntityTerminationContactsEditPanel';
import EntityTerminationTreasuryEditPanel from './EntityTerminationTreasuryEditPanel';

const useStyles = makeStyles({
  bottomSpacing: { marginBottom: '50px' },
  cardContent: { minWidth: 0, overflow: 'hidden' },
});

interface IEntityTerminationOverviewPageProps {
  loading?: boolean;
  companyCode: string;
}

const EntityTerminationOverviewPage = (props: IEntityTerminationOverviewPageProps): JSX.Element => {
  const { loading, companyCode } = props;
  const classes = useStyles();

  const {
    data,
    loading: overviewLoading,
    error,
  } = useQuery(GET_ENTITY_TERMINATION_OVERVIEW, {
    variables: { companyCode },
    fetchPolicy: 'cache-and-network',
  });

  const overview = data?.entityTerminationOverview;
  const isLoading = !!loading || overviewLoading;

  const [isStatusPanelOpen, setIsStatusPanelOpen] = useState<boolean>(false);
  const [isContactsPanelOpen, setIsContactsPanelOpen] = useState<boolean>(false);
  const [isTreasuryPanelOpen, setIsTreasuryPanelOpen] = useState<boolean>(false);

  const renderStatusEditButton = (): JSX.Element => {
    return (
      <CanAccess requestedAction="EntityTerminationOverview:EditStatusInfo">
        <Button
          appearance="subtle"
          title="Edit Status Information"
          aria-label="Edit Status Information"
          icon={<EditRegular color={iconButtonStyles.color} />}
          onClick={(): void => setIsStatusPanelOpen(true)}
        />
      </CanAccess>
    );
  };

  const renderContactsEditButton = (): JSX.Element => {
    return (
      <CanAccess requestedAction="EntityTerminationOverview:EditContacts">
        <Button
          appearance="subtle"
          title="Edit Contacts"
          aria-label="Edit Contacts"
          icon={<EditRegular color={iconButtonStyles.color} />}
          onClick={(): void => setIsContactsPanelOpen(true)}
        />
      </CanAccess>
    );
  };

  const renderTreasuryEditButton = (): JSX.Element => {
    return (
      <CanAccess requestedAction="EntityTerminationOverview:EditTreasury">
        <Button
          appearance="subtle"
          title="Edit Treasury"
          aria-label="Edit Treasury"
          icon={<EditRegular color={iconButtonStyles.color} />}
          onClick={(): void => setIsTreasuryPanelOpen(true)}
        />
      </CanAccess>
    );
  };

  return (
    <FeatureFlagged flagName="EntityTerminationOverview">
      <div className={`${bodyContentContainer} lightBackground`}>
        {error && <LoadingErrorMessage loading={isLoading} error={error} />}
        <Stack>
          <Stack
            horizontal
            wrap
            horizontalAlign="space-between"
            tokens={{ childrenGap: 20 }}
            className={classes.bottomSpacing}
          >
            <StackColumn>
              <Card title="Information" isLoading={isLoading} headingLevel={2}>
                <Stack horizontal tokens={{ childrenGap: 20 }} className={classes.cardContent}>
                  <StackColumn minWidth="0">
                    <SpanWithLabel labelText="Country:" value={overview?.country} />
                    <SpanWithLabel
                      labelText="Functional Currency:"
                      value={overview?.functionalCurrency}
                    />
                    <SpanWithLabel
                      labelText="Statutory Reporting Currency:"
                      value={overview?.statutoryReportingCurrency}
                    />
                    <SpanWithLabel
                      labelText="Statutory Year End:"
                      value={overview?.statutoryYearEnd}
                    />
                  </StackColumn>
                  <StackColumn minWidth="0">
                    <SpanWithLabel labelText="Auditor:" value={overview?.auditor?.fieldName} />
                    <SpanWithLabel
                      labelText="Reporting Group:"
                      value={overview?.reportingGroup?.fieldName}
                    />
                  </StackColumn>
                </Stack>
              </Card>
            </StackColumn>
            <StackColumn>
              <Card
                title="Status Information"
                isLoading={isLoading}
                headingLevel={2}
                actionButton={renderStatusEditButton()}
              >
                <Stack horizontal tokens={{ childrenGap: 20 }} className={classes.cardContent}>
                  <StackColumn minWidth="0">
                    <SpanWithLabel
                      labelText="Legal Status:"
                      value={overview?.legalStatus?.fieldName}
                    />
                    <SpanWithLabel
                      labelText="Lifecycle Status:"
                      value={overview?.lifecycleStatus?.fieldName}
                    />
                    <SpanWithLabel
                      labelText="Termination Lifecycle Status:"
                      value={overview?.terminationLifecycleStatus?.fieldName}
                    />
                    <SpanWithLabel
                      labelText="Blocker Category:"
                      value={overview?.blockerCategory?.fieldName}
                    />
                  </StackColumn>
                  <StackColumn minWidth="0">
                    <SpanWithLabel
                      labelText="Intake Method:"
                      value={overview?.intakeMethod?.fieldName}
                    />
                    <SpanWithLabel
                      labelText="Anticipated Termination FY:"
                      value={overview?.anticipatedTerminationFY}
                    />
                    <SpanWithLabel
                      labelText="Anticipated Termination Quarter:"
                      value={overview?.anticipatedTerminationQuarter}
                    />
                  </StackColumn>
                </Stack>
              </Card>
            </StackColumn>
          </Stack>

          <Stack
            horizontal
            wrap
            horizontalAlign="space-between"
            tokens={{ childrenGap: 20 }}
            className={classes.bottomSpacing}
          >
            <StackColumn>
              <Card
                title="Contacts"
                isLoading={isLoading}
                headingLevel={2}
                actionButton={renderContactsEditButton()}
              >
                <Stack horizontal tokens={{ childrenGap: 20 }} className={classes.cardContent}>
                  <StackColumn minWidth="0">
                    <SpanWithLabel
                      labelText="Termination Contacts:"
                      value={overview?.terminationContacts
                        ?.map((c: { text: string }) => c.text)
                        .join(', ')}
                    />
                    <SpanWithLabel
                      labelText="CELA Term Contact:"
                      value={overview?.celaTermContact?.text}
                    />
                    <SpanWithLabel
                      labelText="Business Stakeholder:"
                      value={overview?.businessStakeholder?.text}
                    />
                    <SpanWithLabel
                      labelText="Statutory Controller:"
                      value={overview?.statutoryController?.text}
                    />
                  </StackColumn>
                  <StackColumn minWidth="0">
                    <SpanWithLabel
                      labelText="General Comments:"
                      value={overview?.generalComments}
                    />
                  </StackColumn>
                </Stack>
              </Card>
            </StackColumn>
            <StackColumn>
              <Card
                title="Treasury"
                isLoading={isLoading}
                headingLevel={2}
                actionButton={renderTreasuryEditButton()}
              >
                <Stack>
                  <StackColumn>
                    <SpanWithLabel labelText="Bank Account:" value={overview?.bankAccount} />
                    <SpanWithLabel labelText="IHCC:" value={overview?.ihcc} />
                    <SpanWithLabel labelText="USCC:" value={overview?.uscc} />
                    <SpanWithLabel labelText="Loan:" value={overview?.loan} />
                  </StackColumn>
                </Stack>
              </Card>
            </StackColumn>
          </Stack>
        </Stack>

        {isStatusPanelOpen && (
          <EntityTerminationStatusEditPanel
            closePanel={() => setIsStatusPanelOpen(false)}
            companyCode={companyCode}
            hasPrimaryBlocker={!!overview?.hasPrimaryBlocker}
            statusInfo={{
              legalStatus: overview?.legalStatus,
              lifecycleStatus: overview?.lifecycleStatus,
              terminationLifecycleStatus: overview?.terminationLifecycleStatus,
              blockerCategory: overview?.blockerCategory,
              intakeMethod: overview?.intakeMethod,
              anticipatedTerminationFY: overview?.anticipatedTerminationFY,
              anticipatedTerminationQuarter: overview?.anticipatedTerminationQuarter,
            }}
          />
        )}
        {isContactsPanelOpen && (
          <EntityTerminationContactsEditPanel
            closePanel={() => setIsContactsPanelOpen(false)}
            companyCode={companyCode}
            contactsInfo={{
              terminationContacts: overview?.terminationContacts,
              celaTermContact: overview?.celaTermContact,
              businessStakeholder: overview?.businessStakeholder,
              statutoryController: overview?.statutoryController,
              generalComments: overview?.generalComments,
            }}
          />
        )}
        {isTreasuryPanelOpen && (
          <EntityTerminationTreasuryEditPanel
            closePanel={() => setIsTreasuryPanelOpen(false)}
            companyCode={companyCode}
            treasuryInfo={{
              bankAccount: overview?.bankAccount,
              ihcc: overview?.ihcc,
              uscc: overview?.uscc,
              loan: overview?.loan,
            }}
          />
        )}
      </div>
    </FeatureFlagged>
  );
};

export default EntityTerminationOverviewPage;
